import React from 'react';
import { FaSpinner, FaTimes } from 'react-icons/fa';
import AdminLayout from '../layout/AdminLayout';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary' | 'white';
  text?: string;
  fullScreen?: boolean;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  text,
  fullScreen = false,
}) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-4 h-4';
      case 'lg':
        return 'w-8 h-8';
      case 'md':
      default:
        return 'w-6 h-6';
    }
  };

  const getColorClasses = () => {
    switch (color) {
      case 'secondary':
        return 'border-gray-300 border-t-gray-600';
      case 'white':
        return 'border-white/30 border-t-white';
      case 'primary':
      default:
        return 'border-green-200 border-t-green-600';
    }
  };

  const spinner = (
    <div className="flex flex-col items-center justify-center gap-3">
      <div
        className={`${getSizeClasses()} ${getColorClasses()} border-2 border-solid rounded-full animate-spin`}
      />
      {text && (
        <p className={`text-sm font-medium ${color === 'white' ? 'text-white' : 'text-gray-600'}`}>
          {text}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 shadow-xl">{spinner}</div>
      </div>
    );
  }

  return spinner;
};

// Inline loading component for buttons
export const InlineLoading: React.FC<{ text?: string }> = ({ text = 'Yükleniyor...' }) => (
  <div className="flex items-center justify-center gap-2">
    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
    <span>{text}</span>
  </div>
);

// Order Detail Loading Component
export const OrderDetailLoading: React.FC<{ title?: string }> = ({ title = 'Sipariş Detayı' }) => (
  <AdminLayout title={title}>
    <div className="flex items-center justify-center min-h-64">
      <div className="text-center">
        <FaSpinner className="animate-spin text-3xl text-blue-500 mx-auto mb-3" />
        <p className="text-gray-600">Sipariş yükleniyor...</p>
      </div>
    </div>
  </AdminLayout>
);

// Order Detail Error Component
export const OrderDetailError: React.FC<{
  error?: string | null;
  onBackClick: () => void;
  title?: string;
}> = ({ error, onBackClick, title = 'Sipariş Detayı' }) => (
  <AdminLayout title={title}>
    <div className="flex items-center justify-center min-h-64">
      <div className="text-center">
        <FaTimes className="text-3xl text-red-500 mx-auto mb-3" />
        <p className="text-red-600 mb-4">{error || 'Sipariş bulunamadı'}</p>
        <button
          onClick={onBackClick}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Sipariş Listesine Dön
        </button>
      </div>
    </div>
  </AdminLayout>
);

export default LoadingSpinner;
